import React from "react";
import { LlmHealthCheck } from "../models/LlmHealthCheck";

// Performance thresholds (in milliseconds)
const PERFORMANCE_THRESHOLDS = {
  EXCELLENT_MAX: 3000,
  GOOD_MAX: 5000,
  // >= 5000ms = Slow
} as const;

// Display configuration
const DISPLAY_CONFIG = {
  MAX_MINI_CHECKS: 20, // Number of checks to show in mini status bar
  MINI_BAR_MIN_WIDTH: 1, // Minimum width for mini bar segments in pixels
} as const;

interface LlmHealthVisualizationProps {
  healthChecks: LlmHealthCheck[];
  timeRangeLabel?: string;
}

export function LlmHealthVisualization({
  healthChecks,
  timeRangeLabel = "Last 1 hour",
}: LlmHealthVisualizationProps) {
  // Group health checks by LlmName/ClientName
  const groupedChecks = React.useMemo(() => {
    const groups = new Map<string, LlmHealthCheck[]>();

    healthChecks.forEach((check) => {
      const key = `${check.llmName}/${check.clientName}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(check);
    });

    return Array.from(groups.entries())
      .map(([key, checks]) => {
        const [llmName, clientName] = key.split("/");
        const sortedChecks = checks.sort(
          (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
        );
        const latestCheck = sortedChecks[sortedChecks.length - 1];
        const healthyChecks = checks.filter((c) => c.isHealthy);
        const avgElapsed =
          checks.reduce((sum, c) => sum + c.elapsedMs, 0) / checks.length;
        const uptimePercentage = (healthyChecks.length / checks.length) * 100;

        return {
          llmName,
          clientName,
          checks: sortedChecks,
          latestCheck,
          avgElapsedMs: Math.round(avgElapsed),
          uptimePercentage: Math.round(uptimePercentage * 100) / 100,
        };
      })
      .sort((a, b) =>
        // Sort by latest status (unhealthy first), then by name
        a.latestCheck.isHealthy === b.latestCheck.isHealthy
          ? a.llmName.localeCompare(b.llmName) ||
            a.clientName.localeCompare(b.clientName)
          : a.latestCheck.isHealthy
            ? 1
            : -1,
      );
  }, [healthChecks]);

  if (groupedChecks.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No health check data available
      </div>
    );
  }

  const statusConfig = [
    {
      condition: (isHealthy: boolean, _: number) => !isHealthy,
      label: "Failed",
      icon: "❌",
      bgColor: "bg-red-50",
      borderColor: "border-red-300",
      dotColor: "bg-red-500",
      textColor: "text-red-700",
    },
    {
      condition: (isHealthy: boolean, elapsedMs: number) =>
        isHealthy && elapsedMs < PERFORMANCE_THRESHOLDS.EXCELLENT_MAX,
      label: "Excellent",
      icon: "✅",
      bgColor: "bg-green-50",
      borderColor: "border-green-300",
      dotColor: "bg-green-500",
      textColor: "text-gray-700",
    },
    {
      condition: (isHealthy: boolean, elapsedMs: number) =>
        isHealthy && elapsedMs < PERFORMANCE_THRESHOLDS.GOOD_MAX,
      label: "Good",
      icon: "⚠️",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-300",
      dotColor: "bg-yellow-500",
      textColor: "text-gray-700",
    },
    {
      condition: (isHealthy: boolean, elapsedMs: number) =>
        isHealthy && elapsedMs >= PERFORMANCE_THRESHOLDS.GOOD_MAX,
      label: "Slow",
      icon: "🐌",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-300",
      dotColor: "bg-orange-500",
      textColor: "text-gray-700",
    },
  ];

  const getStatusInfo = (isHealthy: boolean, elapsedMs: number) => {
    return (
      statusConfig.find((config) => config.condition(isHealthy, elapsedMs)) ||
      statusConfig[statusConfig.length - 1]
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {groupedChecks.map((group) => {
          const latestStatus = getStatusInfo(
            group.latestCheck.isHealthy,
            group.latestCheck.elapsedMs,
          );

          return (
            <div
              key={`${group.llmName}/${group.clientName}`}
              className={`border-2 rounded-lg p-4 ${latestStatus.bgColor} ${latestStatus.borderColor}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{latestStatus.icon}</span>
                    <h3 className="font-semibold text-lg text-gray-900">
                      {group.llmName}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 font-medium">
                    {group.clientName}
                  </p>
                </div>
                <div className="text-right">
                  <div
                    className={`text-sm font-bold ${latestStatus.textColor}`}
                  >
                    {latestStatus.label}
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    Latest Response Time:
                  </span>
                  <span className="font-semibold text-gray-900">
                    {group.latestCheck.elapsedMs}ms
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Last Check:</span>
                  <span className="text-sm text-gray-700">
                    {group.latestCheck.createdAt.toLocaleString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </span>
                </div>

                {group.latestCheck.errorMessage && (
                  <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-xs text-red-700">
                    <strong>Error:</strong> {group.latestCheck.errorMessage}
                  </div>
                )}
              </div>

              <div className="border-t pt-3 mt-3">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="text-center">
                    <div className="text-gray-500">
                      Uptime (
                      {timeRangeLabel.replace("Last ", "").toLowerCase()})
                    </div>
                    <div className="font-semibold text-gray-700">
                      {group.uptimePercentage}%
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500">Avg Response</div>
                    <div className="font-semibold text-gray-700">
                      {group.avgElapsedMs}ms
                    </div>
                  </div>
                </div>

                <div className="mt-2 flex h-2 rounded overflow-hidden bg-gray-200">
                  {group.checks
                    .slice(-DISPLAY_CONFIG.MAX_MINI_CHECKS)
                    .map((check, index) => {
                      const checkStatus = getStatusInfo(
                        check.isHealthy,
                        check.elapsedMs,
                      );
                      return (
                        <div
                          key={index}
                          className={`flex-1 ${checkStatus.dotColor}`}
                          title={`${check.createdAt.toLocaleTimeString()}: ${checkStatus.label} (${check.elapsedMs}ms)`}
                          style={{
                            minWidth: `${DISPLAY_CONFIG.MINI_BAR_MIN_WIDTH}px`,
                          }}
                        />
                      );
                    })}
                </div>
                <div className="text-xs text-gray-400 mt-1 text-center">
                  Last{" "}
                  {Math.min(
                    DISPLAY_CONFIG.MAX_MINI_CHECKS,
                    group.checks.length,
                  )}{" "}
                  checks
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
